import { Routes } from '@angular/router';
import { LoginComponent } from './auth/login/login.component';
import {
  NotfoundComponent,
  AccessDeniedComponent,
  ErrorPageComponent,
} from '@dgacontrol-frontend/sakai-ng';
import { DialogService } from 'primeng/dynamicdialog';
import { authGuardFn, SafeDialogService } from '@dgacontrol-frontend/core';
import {
  AccountsStore,
  ClientProfileStore,
} from '@dgacontrol-frontend/accounts-data-access';
import {
  CatchmentWorkDataStore,
  CatchmentWorkLoraDownlinkStore,
} from '@dgacontrol-frontend/catchment-work-data-data-access';
import {
  MintPlansStore,
  MobileOperatorStore,
  SimcardStore,
} from '@dgacontrol-frontend/cellular-data-access';
import {
  DataloggerStore,
  DownlinkRequestsStore,
  HardwareStore,
  RejectedUplinksStore,
  Rs485MasterStore,
} from '@dgacontrol-frontend/datalogger-data-access';
import {
  GatewayLorawanStore,
  NodesStore,
  RoutersStore,
} from '@dgacontrol-frontend/network-data-access';
import { ConfigurationStore } from '@dgacontrol-frontend/configurations-data-access';
import {
  BillingItemBundlesStore,
  BillingItemsStore,
  QuotesStore,
  QuoteCatchmentWorksStore,
  QuoteInstallationLinesStore,
  QuoteRequestsStore,
  QuoteRequestStatusStore,
  QuoteServiceLinesStore,
  SubscriptionChargeLinesStore,
  SubscriptionChargesStore,
  SubscriptionsStore,
  WorkChargeLinesStore,
  WorkChargesStore,
} from '@dgacontrol-frontend/billing-data-access';
import {
  CommuneStore,
  RegionStore,
} from '@dgacontrol-frontend/geography-data-access';
import {
  LegalAcceptancesStore,
  LegalDocumentsStore,
} from '@dgacontrol-frontend/legal-data-access';

import { deviceDataloggerRouter } from './datalogger/datalogger.routes';
import { networkRoutes } from './network/network.routes';
import { cellularRoutes } from './cellular/cellular.routes';
import { logsRoutes } from './logs/logs.routes';
import { userRoutes } from './users/user.routes';
import { pendingDataRoutes } from './pending-data/peding-data.routes';
import { AdminShellComponent } from './admin-shell/admin-shell.component';
import { QUOTE_REQUESTS_LINK } from './admin-shell/quote-requests-menu';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'users',
    pathMatch: 'full',
  },
  {
    path: '',
    component: AdminShellComponent,
    // Domain stores are scoped to the AUTHENTICATED shell, not to root. Leaving the
    // shell (the only way out is logout → /login) destroys this route's injector and
    // with it every store below, so no state survives into the next user's session.
    // That guarantee used to come from the full page reload in `AuthService.logout()`,
    // which is why the reload could be replaced by a plain `router.navigate`.
    //
    // The load-once cache is NOT lost: this injector outlives navigation BETWEEN the
    // children, so a table and a dropdown still share one instance.
    //
    // Every store an admin component injects must be listed here — a missing one is an
    // NG0201 the moment that screen is opened.
    providers: [
      // HERE, not in `app.config.ts`: a dialog resolves through
      // `DynamicDialogInjector(this.injector)`, and `this.injector` is wherever
      // DialogService was created — so rooting it here is what lets the components
      // opened in a dialog reach the stores below (otherwise NG0201).
      { provide: DialogService, useClass: SafeDialogService },
      AccountsStore,
      // The billing form of a user (dt-users → frm-client-profile, in a dialog).
      ClientProfileStore,
      CatchmentWorkDataStore,
      CatchmentWorkLoraDownlinkStore,
      MintPlansStore,
      MobileOperatorStore,
      SimcardStore,
      DataloggerStore,
      DownlinkRequestsStore,
      HardwareStore,
      RejectedUplinksStore,
      Rs485MasterStore,
      GatewayLorawanStore,
      NodesStore,
      RoutersStore,
      ConfigurationStore,
      BillingItemsStore,
      BillingItemBundlesStore,
      SubscriptionsStore,
      SubscriptionChargesStore,
      SubscriptionChargeLinesStore,
      WorkChargesStore,
      WorkChargeLinesStore,
      QuotesStore,
      QuoteCatchmentWorksStore,
      QuoteInstallationLinesStore,
      QuoteServiceLinesStore,
      // The landing's quote requests: the inbox, and the menu's count (admin-shell).
      QuoteRequestsStore,
      QuoteRequestStatusStore,
      RegionStore,
      CommuneStore,
      LegalDocumentsStore,
      LegalAcceptancesStore,
    ],
    children: [
      {
        path: 'users',
        children: userRoutes,
      },
      {
        path: 'logs',
        children: logsRoutes,
      },
      {
        path: 'pending-data',
        children: pendingDataRoutes,
      },
      {
        path: 'cellular',
        children: cellularRoutes,
      },
      {
        path: 'network',
        children: networkRoutes,
      },
      {
        path: 'dataloggers',
        children: deviceDataloggerRouter,
      },
      {
        path: 'configurations',
        loadChildren: () =>
          import(
            '@dgacontrol-frontend/configurations-feature-configuration'
          ).then((m) => m.CONFIGURATION_ROUTES),
      },
      {
        path: 'cellular/planes-mint',
        loadChildren: () =>
          import(
            '@dgacontrol-frontend/cellular-feature-mint-plans-crud'
          ).then((m) => m.MINT_PLANS_ROUTES),
      },
      {
        path: 'billing/items',
        loadChildren: () =>
          import(
            '@dgacontrol-frontend/billing-feature-billing-items'
          ).then((m) => m.BILLING_ITEMS_ROUTES),
      },
      {
        path: 'billing/item-bundles',
        loadChildren: () =>
          import(
            '@dgacontrol-frontend/billing-feature-billing-item-bundles'
          ).then((m) => m.BILLING_ITEM_BUNDLES_ROUTES),
      },
      {
        path: 'billing/subscriptions',
        loadChildren: () =>
          import(
            '@dgacontrol-frontend/billing-feature-subscriptions'
          ).then((m) => m.SUBSCRIPTIONS_ROUTES),
      },
      {
        path: 'billing/work-charges',
        loadChildren: () =>
          import(
            '@dgacontrol-frontend/billing-feature-work-charges'
          ).then((m) => m.WORK_CHARGES_ROUTES),
      },
      // Before M-bill-36 each kind had its own screen: saved links land on the one.
      { path: 'billing/setup-charges', redirectTo: '/billing/work-charges' },
      { path: 'billing/service-charges', redirectTo: '/billing/work-charges' },
      {
        path: 'billing/quotes',
        loadChildren: () =>
          import('@dgacontrol-frontend/billing-feature-quotes').then(
            (m) => m.QUOTES_ROUTES,
          ),
      },
      {
        path: QUOTE_REQUESTS_LINK,
        loadChildren: () =>
          import('@dgacontrol-frontend/billing-feature-quotes').then(
            (m) => m.QUOTE_REQUESTS_ROUTES,
          ),
      },
      {
        path: 'legal/documents',
        loadChildren: () =>
          import('@dgacontrol-frontend/legal-feature-documents').then(
            (m) => m.LEGAL_DOCUMENTS_ROUTES,
          ),
      },
      {
        path: 'legal/acceptances',
        loadChildren: () =>
          import('@dgacontrol-frontend/legal-feature-acceptances').then(
            (m) => m.LEGAL_ACCEPTANCES_ROUTES,
          ),
      },
      {
        path: 'geography/regions',
        loadChildren: () =>
          import('@dgacontrol-frontend/geography-feature-regions').then(
            (m) => m.REGIONS_ROUTES,
          ),
      },
      {
        path: 'geography/communes',
        loadChildren: () =>
          import('@dgacontrol-frontend/geography-feature-communes').then(
            (m) => m.COMMUNES_ROUTES,
          ),
      },
    ],
    canActivate: [authGuardFn],
  },
  {
    path: 'login',
    component: LoginComponent,
  },
  { path: 'notfound', component: NotfoundComponent },
  { path: 'access-denied', component: AccessDeniedComponent },
  { path: 'error', component: ErrorPageComponent },
  { path: '**', redirectTo: '/notfound' },
];
